import type { LeadApiErrorResponse, LeadApiFieldError } from "~/types/types";

export const LEAD_SUBMISSION_ERROR_MESSAGE = "We couldn't submit your request. Please try again.";

export const LEAD_API_EMAIL_FIELD = "email_address";
export const LEAD_API_PHONE_FIELD = "phone";

const FIELD_MESSAGES: Record<string, string> = {
  [LEAD_API_EMAIL_FIELD]: "Please enter a valid email address.",
  [LEAD_API_PHONE_FIELD]: "Please enter a valid phone number and try again."
};

export interface LeadApiErrorMessages {
  email: string;
  phone: string;
  general: string;
}

type LeadApiErrorPayload = {
  statusCode?: number;
  response?: { status?: number; data?: LeadApiErrorResponse };
  data?: LeadApiErrorResponse;
};

function getFieldErrors(error: unknown): LeadApiFieldError[] {
  const payload = error as LeadApiErrorPayload | null | undefined;
  return payload?.response?.data?.errors ?? payload?.data?.errors ?? [];
}

export function parseLeadApiErrors(error: unknown): LeadApiErrorMessages {
  const fieldErrors = getFieldErrors(error);

  const messages: LeadApiErrorMessages = { email: "", phone: "", general: "" };
  let hasUnmappedError = fieldErrors.length === 0;

  fieldErrors.forEach(({ field }) => {
    if (field === LEAD_API_EMAIL_FIELD) {
      messages.email = FIELD_MESSAGES[LEAD_API_EMAIL_FIELD];
    } else if (field === LEAD_API_PHONE_FIELD) {
      messages.phone = FIELD_MESSAGES[LEAD_API_PHONE_FIELD];
    } else {
      hasUnmappedError = true;
    }
  });

  if (hasUnmappedError) {
    messages.general = LEAD_SUBMISSION_ERROR_MESSAGE;
  }

  return messages;
}

export interface LeadApiRejectionReport {
  message: string;
  fingerprint: string[];
}

/**
 * Describes a 400 from the lead API for Sentry, grouped by the field names the
 * API rejected. Only field names are used: the values and the API's messages
 * can echo what the visitor typed. Returns null for any other failure, which
 * httpClientIntegration already reports.
 */
export function getLeadApiRejectionReport(error: unknown): LeadApiRejectionReport | null {
  const payload = error as LeadApiErrorPayload | null | undefined;
  const status = payload?.statusCode ?? payload?.response?.status;
  if (status !== 400) return null;

  const fieldNames = [
    ...new Set(
      getFieldErrors(error)
        .map(({ field }) => field)
        .filter((field) => typeof field === "string" && field !== "")
    )
  ].sort();
  const fields = fieldNames.length > 0 ? fieldNames : ["unmapped"];

  return {
    message: `Lead API rejected submission (fields: ${fields.join(", ")})`,
    fingerprint: ["lead-api-rejection", ...fields]
  };
}
